import dayjs from 'dayjs';
import { calculateDateRange } from '../../utils/dates';
import { DATE_RANGE_PARAM_FORMAT } from '../../utils/periodParams';
import { NotificationsConstants, TYPE_TABS, VALID_NOTIFICATION_TIMEFRAMES } from './notifications.enums';

/**
 * Pure helpers for the Notifications screen. Kept free of React/SCSS so they
 * are unit-testable (see tests/notificationsUtils.test.js).
 */

/** `?type=` → a valid tab key (falls back to 'all'). */
export function parseTypeTab(raw) {
    return TYPE_TABS.some((t) => t.key === raw) ? raw : NotificationsConstants.LIST_TYPE_DEFAULT;
}

/** `?unread=` → boolean ('1' / 'true' are on). */
export function parseUnreadParam(raw) {
    if (raw == null) return false;
    const v = String(raw).toLowerCase();
    return v === '1' || v === 'true';
}

/**
 * `?period=` + `?range=` → `{ timeFrame, dateRange }` for `DateFilters`.
 * Mirrors `utils/periodParams.parsePeriodParams` for the All/7d/30d/Custom
 * frame set that `DateFilters` offers.
 */
export function parseNotificationsPeriod(periodParam, rangeParam) {
    const fallback = NotificationsConstants.TIMEFRAME_DEFAULT;
    const timeFrame = VALID_NOTIFICATION_TIMEFRAMES.includes(periodParam) ? periodParam : fallback;
    if (timeFrame !== 'custom') return { timeFrame, dateRange: null };
    if (!rangeParam) return { timeFrame: fallback, dateRange: null };

    const [rawStart, rawEnd] = String(rangeParam).split(',');
    const start = dayjs(rawStart, DATE_RANGE_PARAM_FORMAT);
    const end = dayjs(rawEnd, DATE_RANGE_PARAM_FORMAT);
    if (!rawStart || !rawEnd || !start.isValid() || !end.isValid()) {
        return { timeFrame: fallback, dateRange: null };
    }
    return { timeFrame: 'custom', dateRange: [start, end] };
}

/** `{ timeFrame, dateRange }` → URL values (`range` is null unless a full custom range). */
export function serializeNotificationsPeriod(timeFrame, dateRange) {
    const period = timeFrame && timeFrame !== NotificationsConstants.TIMEFRAME_DEFAULT ? timeFrame : null;
    if (timeFrame !== 'custom' || !dateRange?.[0] || !dateRange?.[1]) {
        return { period, range: null };
    }
    return {
        period,
        range: [dateRange[0].format(DATE_RANGE_PARAM_FORMAT), dateRange[1].format(DATE_RANGE_PARAM_FORMAT)],
    };
}

/**
 * Screen state → query for `data.fetchNotifications`. Omits `type` for
 * 'all', `unread` when off, and `start`/`end` when the frame is 'all' or an
 * incomplete custom range.
 */
export function buildNotificationsQuery({ page = 1, perPage, typeTab, unreadOnly, timeFrame, dateRange }) {
    const query = { page, perPage: perPage || NotificationsConstants.DEFAULT_PAGE_SIZE };
    if (typeTab && typeTab !== NotificationsConstants.LIST_TYPE_DEFAULT) query.type = typeTab;
    if (unreadOnly) query.unread = true;
    const { start, end } = calculateDateRange(timeFrame || NotificationsConstants.TIMEFRAME_DEFAULT, dateRange);
    if (start && end) {
        query.start = start;
        query.end = end;
    }
    return query;
}
